import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from './graphSlotLayout';
import { GraphData } from './schemas';

/** 期間推移グラフのデフォルト項目コード（粗利合計(千円)）。API に field 未指定時のバックエンド既定と一致させる */
export const DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD = 'arariGk1000Base';

/** 推移グラフ（期間推移）で同時表示できるグラフ項目の最大数 */
export const DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS = 5;

/** 期間推移の複数グラフで recharts syncId に渡す固定値 */
export const DATA_TERMINAL_TRANSITION_GRAPH_SYNC_ID = 'terminalTransitionGraph';

/** 1本表示時のグラフ高さ（px） */
export const DATA_TERMINAL_TRANSITION_GRAPH_CHART_HEIGHT = 360;

/** 2本表示時の1本あたりのグラフ高さ（px） */
export const DATA_TERMINAL_TRANSITION_GRAPH_COMPACT_CHART_HEIGHT = 200;

/** 3本表示時の1本あたりのグラフ高さ（px） */
export const DATA_TERMINAL_TRANSITION_GRAPH_DENSE_CHART_HEIGHT = 140;

/** 4本以上表示時の1本あたりのグラフ高さ（px） */
export const DATA_TERMINAL_TRANSITION_GRAPH_OVERFLOW_CHART_HEIGHT = 120;

/**
 * 表示本数（スロット数）に応じたグラフ高さ（px）を返す。
 */
export const resolveDataTerminalTransitionGraphChartHeight = (
  graphCount: number
): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: DATA_TERMINAL_TRANSITION_GRAPH_CHART_HEIGHT,
    double: DATA_TERMINAL_TRANSITION_GRAPH_COMPACT_CHART_HEIGHT,
    triple: DATA_TERMINAL_TRANSITION_GRAPH_DENSE_CHART_HEIGHT,
    overflow: DATA_TERMINAL_TRANSITION_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

/** タイトル・共通条件・枠線など、グラフ本体以外の高さ（px） */
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_TITLE_HEIGHT = 40;
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_CONTROLS_HEIGHT = 88;
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 * 機種集計と同様、グラフ本数に応じて領域を広げる。
 */
export const resolveDataTerminalTransitionGraphContainerHeight = (
  slotCount: number
): number =>
  resolveGraphSlotContainerHeight(slotCount, {
    chromeHeight: DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_CHROME_HEIGHT,
    titleHeight: DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_TITLE_HEIGHT,
    controlsHeight: DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_CONTROLS_HEIGHT,
    slotControlsHeight:
      DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
    slotGap: DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_SLOT_GAP,
    footerHeight: DATA_TERMINAL_TRANSITION_GRAPH_CONTAINER_FOOTER_HEIGHT,
    resolveChartHeight: resolveDataTerminalTransitionGraphChartHeight,
  });

export type DataTerminalTransitionGraphFieldItem = {
  field: string;
  data: GraphData;
};

/**
 * 期間推移 推移グラフ検索条件の型
 */
export type DataTerminalTransitionGraphParams = {
  /**
   * グラフ API のクエリ param `field`（1項目分）。
   * 複数グラフ表示以前のお気に入りはこちらが保存されている。
   */
  field?: string;
  /**
   * 画面に出すグラフ項目の一覧（最大 DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS 件）。
   * お気に入り保存用。API リクエストでは使わず、Saga が1件ずつ field に分けて送る。
   */
  fields?: string[];
  /**
   * エリアID一覧(未指定で全エリア)
   */
  areas?: string[];
  /**
   * 対象店舗コード(未指定で全店舗)
   */
  halls?: string[];
  /**
   * anyを含む日付タイプ(Available values : daily, weekly, monthly, any)
   */
  dateType?: string;
  /**
   * dateType=anyの時に何日ごとに集計するか
   */
  durationForAnyDateType?: number;
  /**
   * 集計する日付範囲の開始日(デフォルト:当月の開始日)
   */
  startDate?: string;
  /**
   * 集計する日付範囲の最終日(デフォルト:当日)
   */
  endDate?: string;
  /**
   * 表示日付範囲に当日を除外するかどうか(デフォルト:true)
   */
  excludeToday?: boolean;
  /**
   * 曜日の一覧(未指定で全て)
   */
  days?: string[];
  /**
   * 平日のみか休日のみか全てか(未指定で全て)
   */
  dayType?: string;
  /**
   * 日付の末尾(未指定で全て)
   */
  dateSuffixes?: number[];
  /**
   * 日にち(未指定で全て)
   */
  dates?: number[];
  /**
   * 種別グループID(未指定で全種別グループ)
   */
  shuGroupIds?: string[];
  /**
   * 種別一覧(未指定で全種別)
   */
  shuList?: string[];
};

/**
 * Redux に保持する検索条件（期間・店舗・種別など）。
 * どのグラフ項目を表示するかは graphList[].field に持ち、setting には含めない（二重管理を避ける）。
 */
export type DataTerminalTransitionGraphSetting = Omit<
  DataTerminalTransitionGraphParams,
  'field' | 'fields'
>;

/**
 * 期間推移 推移グラフの型
 */
export type DataTerminalTransitionGraph = {
  setting: DataTerminalTransitionGraphSetting;
  graphList: DataTerminalTransitionGraphFieldItem[];
};

/**
 * API レスポンスの setting から Redux 用 setting を作る。
 * field / fields は graphList[].field に一本化するため取り除く。
 */
export const toDataTerminalTransitionGraphSetting = (
  params: DataTerminalTransitionGraphParams
): DataTerminalTransitionGraphSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};
